import api from './api';
import { tasksApi, type Task, type TaskCreatePayload } from './tasks';

export type MeetlyMeetingStatus = 'SUBMITTING' | 'QUEUED' | 'COMPLETED' | 'FAILED';
export type MeetlyMeetingStatusRaw = MeetlyMeetingStatus
  | 'PROCESSING'
  | 'TRANSCRIBING'
  | 'PROCESSING_AUDIO'
  | 'SUMMARIZING'
  | 'GENERATING_SUMMARY'
  | 'UPLOADING'
  | 'AUDIO_PROCESSING_FAILED'
  | 'RECORDING_FAILED'
  | 'UPLOAD_FAILED'
  | 'TRANSCRIPTION_FAILED'
  | 'SUMMARY_FAILED'
  | 'UPLOADED'
  | 'RECORDING';

export const normalizeMeetlyMeetingStatus = (status: MeetlyMeetingStatusRaw): MeetlyMeetingStatus => {
  if (status === 'FAILED'
    || status === 'AUDIO_PROCESSING_FAILED'
    || status === 'RECORDING_FAILED'
    || status === 'UPLOAD_FAILED'
    || status === 'TRANSCRIPTION_FAILED'
    || status === 'SUMMARY_FAILED') {
    return 'FAILED';
  }

  if (status === 'SUBMITTING'
    || status === 'UPLOADING'
    || status === 'UPLOADED'
    || status === 'RECORDING') {
    return 'SUBMITTING';
  }

  if (status === 'PROCESSING'
    || status === 'PROCESSING_AUDIO'
    || status === 'TRANSCRIBING'
    || status === 'SUMMARIZING'
    || status === 'GENERATING_SUMMARY'
    || status === 'QUEUED') {
    return 'QUEUED';
  }

  return status;
};

export interface MeetlyActionItem {
  id: string;
  text: string;
  owner?: string | null;
  task?: string | null;
  due?: string | null;
  suggested_assignee: string | null;
  suggested_due_date: string | null;
  segment_timestamp?: string | null;
  order_index: number;
  created_task_id: string | null;
  task_created: boolean;
}

export interface MeetlyContextNote {
  id: string;
  text: string;
  created_at: string;
  updated_at: string;
}

export interface MeetlyShareUser {
  user_id: string;
  name: string;
  email: string | null;
}

export type MeetlyMeetingScope = 'mine' | 'shared';

/**
 * The one pipeline vocabulary shared by the worker, this website, the desktop app
 * and the phone app. `status` above stays a four-value summary because the phone
 * app switches on it; this is the granular position used to draw progress.
 */
export type MeetlyProcessingStage =
  | 'RECORDING'
  | 'PROCESSING'
  | 'TRANSCRIBING'
  | 'TRANSCRIPT_READY'
  | 'SUMMARIZING'
  | 'COMPLETED'
  | 'FAILED';

export const MEETLY_STAGE_ORDER: MeetlyProcessingStage[] = [
  'RECORDING',
  'PROCESSING',
  'TRANSCRIBING',
  'TRANSCRIPT_READY',
  'SUMMARIZING',
  'COMPLETED',
];

export const isTerminalStage = (stage: MeetlyProcessingStage) =>
  stage === 'COMPLETED' || stage === 'FAILED';

export interface MeetlyMeeting {
  id: string;
  title: string | null;
  status: MeetlyMeetingStatusRaw;
  // Older backends do not send these; callers must tolerate their absence.
  processing_stage?: MeetlyProcessingStage;
  has_transcript?: boolean;
  summary: string | null;
  action_items: MeetlyActionItem[];
  error: string | null;
  language: string | null;
  summary_template: string | null;
  context_notes: MeetlyContextNote[];
  duration_seconds: number | null;
  capture_mode: string | null;
  /** How much of the recording was heard; null on the list and for imported meetings. */
  audio_report?: MeetlyAudioReport | null;
  submitted_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // Sharing (Meeting → Share). Older backends omit these.
  /** Who recorded it. */
  owner_id?: string;
  /** Set on a meeting someone else shared with me (read-only). */
  owner_name?: string | null;
  is_shared?: boolean;
  /** Owner side: who can read it. Empty on a meeting shared with me. */
  shared_with?: MeetlyShareUser[];
}

// The summariser never produces a bespoke meeting title, so the closest
// thing to a real name is the template it was run with.
const SUMMARY_TEMPLATE_LABELS: Record<string, string> = {
  project_sync: 'Project sync meeting',
  daily_standup: 'Daily standup meeting',
  client_call: 'Client call meeting',
  general: 'General meeting',
};

export const meetlyMeetingTitle = (meeting: MeetlyMeeting) =>
  meeting.title?.trim()
  || SUMMARY_TEMPLATE_LABELS[meeting.summary_template || '']
  || 'Meeting';

export const CAPTURE_MODE_LABELS: Record<string, string> = {
  microphone: 'Microphone only',
  'microphone-and-system': 'Microphone + system audio',
  imported: 'Imported audio file',
};

export interface MeetlyAudioReport {
  level: 'good' | 'mixed' | 'poor';
  verdict: string;
  parts: number;
  transcribed: number;
  silent: number;
  quiet: number;
  failed: number;
  mean_db: number | null;
  capture_mode: string | null;
}

export interface MeetlyMeetingResponse {
  meeting: MeetlyMeeting;
}

export interface MeetlyJobSubmissionResponse {
  job_id: string;
  meeting_id: string;
  accepted: boolean;
  status?: string;
}

export interface MeetlyTranscriptResponse {
  meeting_id: string;
  transcript: string;
}

export interface MeetlyMeetingUpdate {
  title: string;
  summary: string;
  action_items: Array<{
    id?: string;
    text: string;
    owner?: string | null;
    task?: string | null;
    due?: string | null;
  }>;
}

export interface MeetlyStandardResponse {
  success: boolean;
  message: string;
  data: Record<string, unknown>;
  errorCode: string | null;
  processingStatus: string | null;
  progress: Record<string, unknown> | null;
  retryable: boolean;
}

export interface MeetlyMeetingListResponse {
  meetings: MeetlyMeeting[];
  total: number;
  page: number;
  page_size: number;
}

export interface MeetlyActionItemTaskResponse {
  action_item: MeetlyActionItem;
  task: Task;
}

// `workspace` is the superadmin's company-wide ceiling: null when nobody has
// granted one. `remaining` is already the tighter of the personal and the
// workspace figure, so a meter can read it directly — `workspace` is there to
// explain WHICH limit bit when the personal one still looks healthy.
export interface MeetlyWorkspacePool { limit: number; used: number; reserved: number; remaining: number; }
export interface MeetlyCredits { ok: boolean; limit: number; used: number; reserved: number; remaining: number; workspace: MeetlyWorkspacePool | null; source: string; locked_by_admin: boolean; period_start: string; period_end: string; resets_on: string; }
export interface MeetlyUsageTotals { prompt_tokens: number; completion_tokens: number; total_tokens: number; calls: number; failed: number; messages: number; }
export interface MeetlyUsage { scope: 'self' | 'team' | 'branch'; start: string; end: string; totals: MeetlyUsageTotals; by_day: Array<{ date: string } & MeetlyUsageTotals>; people: Array<{ user_id: string; name: string } & MeetlyUsageTotals>; }
// `meetly_enabled` is the person's effective entitlement (per-employee override,
// else their Access profile). The branch list deliberately includes people it is
// false for, so an admin can enable and fund someone in one visit.
export interface MeetlyLimitsPerson { user_id: string; monthly_credits: number; locked_by_admin: boolean; can_edit: boolean; meetly_enabled: boolean; can_manage_access: boolean; }
export interface MeetlyLimits { scope: 'self' | 'team' | 'branch'; self: MeetlyCredits; people: MeetlyLimitsPerson[]; }

export interface MeetlySession {
  session_id: string;
  meeting_id: string;
  job_id: string;
  resumed: boolean;
}

export interface MeetlyChunkAck {
  session_id: string;
  chunk_index: number;
  received: number;
  total: number;
  duplicate: boolean;
}

export interface MeetlyChunkProgress {
  total: number;
  received: number;
  pending: number;
  transcribing: number;
  transcribed: number;
  /** Gemini heard no speech in the chunk (quiet mic, a pause, a muted call). */
  silent: number;
  failed: number;
  /** Chunks whose measured level was below the server's quiet threshold. */
  quiet: number;
  /** The chunk that just finished, with the words it heard — on realtime frames. */
  last_chunk?: MeetlyChunkView;
}

export type MeetlyChunkState = 'pending' | 'transcribing' | 'transcribed' | 'silent' | 'failed';

/** One chunk as GET /meetly/sessions/{id}/chunks and the progress frame carry it. */
export interface MeetlyChunkView {
  chunk_index: number;
  state: MeetlyChunkState;
  start_time: number | null;
  end_time: number | null;
  duration: number | null;
  size_bytes: number;
  /** Mean level in dBFS measured before transcription; null until measured. */
  level_db: number | null;
  peak_db: number | null;
  quiet: boolean;
  words: number;
  text: string;
  error: string | null;
}

export interface MeetlySessionChunks {
  session_id: string;
  meeting_id: string;
  status: string | null;
  processing_stage: MeetlyProcessingStage | null;
  progress: MeetlyChunkProgress;
  chunks: MeetlyChunkView[];
}

export interface MeetlySessionStatus {
  session_id: string;
  meeting_id: string;
  status: string | null;
  processing_stage: MeetlyProcessingStage | null;
  received_chunks: number;
  total_chunks: number;
  chunk_progress: Partial<MeetlyChunkProgress>;
  missing_chunks: number[];
}

export interface MeetlyChunkUpload {
  blob: Blob;
  chunkIndex: number;
  chunkUuid: string;
  startTime: number;
  endTime: number;
  duration: number;
  mimeType: string;
  extension: string;
}

export const getMeetlyCredits = () => api.get<MeetlyCredits>('/meetly/credits');
export const getMeetlyUsage = (start?: string, end?: string) => api.get<MeetlyUsage>('/meetly/usage', { params: { start, end } });
export const getMeetlyLimits = () => api.get<MeetlyLimits>('/meetly/limits');
export const setMeetlyLimit = (userId: string, monthlyCredits: number) => api.put(`/meetly/limits/${encodeURIComponent(userId)}`, { monthly_credits: monthlyCredits });
export const setMeetlyAccess = (userId: string, enabled: boolean) => api.put<{ user_id: string; meetly_enabled: boolean }>(`/meetly/access/${encodeURIComponent(userId)}`, { enabled });

// "X shared a meeting with you" alerts ride the shared notification bell via a
// namespaced task_id "meeting:<meeting_id>" — the twin of the note feed.
export const getMeetlyNotifications = (limit = 50) =>
  api.get(`/meetly/notifications/unread?limit=${limit}`);
export const markMeetlyNotificationRead = (id: string) =>
  api.post(`/meetly/notifications/${encodeURIComponent(id)}/read`);

const newMeetingId = () => {
  const randomId = globalThis.crypto?.randomUUID?.();
  return `meeting-${randomId || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`}`;
};

export const meetlyApi = {
  createMeeting: (
    file: File,
    options: {
      language?: string;
      summary_template?: string;
      duration_seconds?: number;
      capture_mode?: string;
    } = {},
  ) => {
    const formData = new FormData();
    formData.append('meeting_id', newMeetingId());
    formData.append('file', file);
    formData.append('language', options.language || 'en');
    formData.append('summary_template', options.summary_template || 'project_sync');
    if (options.duration_seconds !== undefined) {
      formData.append('duration_seconds', String(options.duration_seconds));
    }
    if (options.capture_mode) {
      formData.append('capture_mode', options.capture_mode);
    }
    return api.post<MeetlyJobSubmissionResponse>('/meetly/jobs', formData, {
      // Meeting captures can be much larger than ordinary JSON requests. Let
      // the request complete instead of surfacing a false 30-second failure
      // while Meetly continues processing the same upload server-side.
      timeout: 0,
    });
  },

  /**
   * Open a chunked recording session.
   *
   * Passing the same meeting_id back rejoins the existing session instead of
   * opening a second one, which is what makes a reconnecting recorder safe.
   */
  createSession: (options: {
    meeting_id?: string;
    language?: string;
    summary_template?: string;
    mime_type?: string;
    capture_mode?: string;
    title?: string;
  } = {}) => api.post<MeetlySession>('/meetly/sessions', {
    meeting_id: options.meeting_id,
    language: options.language || 'en',
    summary_template: options.summary_template || 'project_sync',
    mime_type: options.mime_type,
    capture_mode: options.capture_mode,
    title: options.title,
  }),

  uploadChunk: (sessionId: string, chunk: MeetlyChunkUpload, signal?: AbortSignal) => {
    const formData = new FormData();
    formData.append(
      'file',
      chunk.blob,
      `chunk-${String(chunk.chunkIndex).padStart(4, '0')}.${chunk.extension}`,
    );
    formData.append('chunk_index', String(chunk.chunkIndex));
    formData.append('chunk_uuid', chunk.chunkUuid);
    formData.append('start_time', String(chunk.startTime));
    formData.append('end_time', String(chunk.endTime));
    formData.append('duration', String(chunk.duration));
    return api.post<MeetlyChunkAck>(
      `/meetly/sessions/${encodeURIComponent(sessionId)}/chunks`,
      formData,
      // A chunk is small, but a bad network can still take a while. This is a
      // per-chunk timeout, not the meeting-sized one the whole-file path needs.
      { timeout: 120_000, signal },
    );
  },

  finalizeSession: (sessionId: string, options: { total_chunks?: number; force?: boolean } = {}) =>
    api.post<{ session_id: string; meeting_id: string; status: string | null }>(
      `/meetly/sessions/${encodeURIComponent(sessionId)}/finalize`,
      { total_chunks: options.total_chunks, force: Boolean(options.force) },
      { timeout: 60_000 },
    ),

  getSession: (sessionId: string) =>
    api.get<MeetlySessionStatus>(`/meetly/sessions/${encodeURIComponent(sessionId)}`),

  getSessionChunks: (sessionId: string) =>
    api.get<MeetlySessionChunks>(`/meetly/sessions/${encodeURIComponent(sessionId)}/chunks`),

  // `scope: 'shared'` lists meetings colleagues shared with me (read-only);
  // it is the one list a colleague without Meetly can load.
  listMeetings: (params: { page?: number; page_size?: number; scope?: MeetlyMeetingScope } = {}) =>
    api.get<MeetlyMeetingListResponse>('/meetly/meetings', { params }),

  getMeeting: (meetingId: string) =>
    api.get<MeetlyMeetingResponse>(`/meetly/meetings/${encodeURIComponent(meetingId)}`),

  getTranscript: (meetingId: string) =>
    api.get<MeetlyTranscriptResponse>(`/meetly/meetings/${encodeURIComponent(meetingId)}/transcript`),

  updateMeeting: (meetingId: string, payload: MeetlyMeetingUpdate) =>
    api.put<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}`,
      {
        title: payload.title,
        summary: payload.summary,
        action_items: payload.action_items.map(({ id, text, owner, task, due }) => ({
          id,
          text,
          owner,
          task,
          due,
        })),
      },
    ),

  deleteMeeting: (meetingId: string) =>
    api.delete<{ success: true }>(`/meetly/meetings/${encodeURIComponent(meetingId)}`),

  // Replaces the recipient set: send everyone who should keep access.
  shareMeeting: (meetingId: string, userIds: string[]) =>
    api.post<{ ok: boolean; shared_with: MeetlyShareUser[] }>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/share`,
      { user_ids: userIds },
    ),

  // Kicks off regeneration and returns a job envelope, not the finished
  // meeting — the caller re-polls getMeeting for the new summary.
  regenerateSummary: (
    meetingId: string,
    options: { language?: string; summary_template?: string } = {},
  ) =>
    api.post<MeetlyStandardResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/regenerate`,
      options,
    ),

  addContextNote: (meetingId: string, text: string) =>
    api.post<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/context`,
      { text },
    ),

  updateContextNote: (meetingId: string, noteId: string, text: string) =>
    api.put<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/context/${encodeURIComponent(noteId)}`,
      { text },
    ),

  deleteContextNote: (meetingId: string, noteId: string) =>
    api.delete<MeetlyMeetingResponse>(
      `/meetly/meetings/${encodeURIComponent(meetingId)}/context/${encodeURIComponent(noteId)}`,
    ),

  // Lio-Notes owns meeting data; creating a Teamlio task uses the ordinary
  // authenticated task endpoint and never needs an internal Meetly route.
  createTaskFromActionItem: (
    _meetingId: string,
    actionItemId: string,
    task: TaskCreatePayload,
  ) => tasksApi.create(task).then(response => ({
    ...response,
    data: {
      task: response.data as Task,
      action_item: { id: actionItemId } as MeetlyActionItem,
    },
  })),
};

export default meetlyApi;
